import { useCallback, useMemo, useState } from "react";
import StudentTag from "../../domains/StudentTag";

// `useMemo` の依存安定化のため、デフォルトの空配列はモジュールスコープで共有する
const EMPTY_HISTORY_TAGS: StudentTag[] = [];

/**
 * 校舎一斉送信ページの「現在開かれている宛先タグ」管理フック。
 *
 * `tags`: 新規送信時の宛先候補（allTags、論理削除タグを含まない）
 * `historyTags`: 過去履歴の宛先（destinationTags、論理削除タグも含む。TAG_FETCH-04）
 *
 * URL の `tagId` を解決する際、両方の候補から検索する。過去履歴経由でアクセスされた
 * 論理削除タグも `openedTag` として解決され、`deleted: true` 情報が形式に伝播する
 * （TAG_HISTORY-01 が機能するために必要）。
 */
export const useAnnounceDestinationTag = ({
  tags,
  historyTags = EMPTY_HISTORY_TAGS,
}: {
  tags: StudentTag[];
  historyTags?: StudentTag[];
}) => {
  const [openedTagId, setOpenedTagId] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const additions = historyTags.filter(
      (h) => !tags.some((t) => t.id === h.id && t.type === h.type),
    );
    return [...tags, ...additions];
  }, [tags, historyTags]);

  const closeAnnounceDestinationTag = useCallback(() => {
    setOpenedTagId(null);
  }, []);

  const openAnnounceDestinationTagById = useCallback(
    (id: string) => {
      const destinationTag = candidates.find((tag) => tag.id === id);
      if (destinationTag) {
        setOpenedTagId(destinationTag.id);
      }
    },
    [candidates],
  );

  const openedTag = useMemo(
    () => candidates.find((tag) => tag.id === openedTagId) ?? null,
    [candidates, openedTagId],
  );

  return {
    openedTag,
    openAnnounceDestinationTagById,
    closeAnnounceDestinationTag,
  };
};
